import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { siteMetadata } from '@/config/metaTags';
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from '@/config/languages';
import StructuredData, { Schema } from './StructuredData';
import Robot from './Robot';

interface SEOHeadProps {
  title?: string;
  description?: string;
  image?: string;
  keywords?: string;
  type?: 'website' | 'article' | 'product';
  schema?: Schema;
  noindex?: boolean;
  nofollow?: boolean;
  breadcrumbs?: Array<{ name: string; url: string }>;
}

/**
 * Enhanced SEOHead component that accepts dynamic props for page-specific metadata.
 * It falls back to default site-wide metadata if no props are provided.
 */
const SEOHead = ({
  title,
  description,
  image,
  keywords,
  type = 'website',
  schema,
  noindex = false,
  nofollow = false
}: SEOHeadProps = {}) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const currentUrl = `${siteMetadata.siteUrl}${currentPath}`;

  const pageTitle = title ? `${title} | ${siteMetadata.title}` : siteMetadata.title;
  const pageDescription = description || siteMetadata.description;
  const pageImage = image || siteMetadata.image;
  const pageKeywords = keywords || siteMetadata.keywords;

  // Calculate base path for hreflang tags
  const getBasePath = (path: string) => {
    const segments = path.split('/').filter(Boolean);
    const first = segments[0];
    if (first && SUPPORTED_LANGUAGES.includes(first as any)) {
      return '/' + segments.slice(1).join('/');
    }
    return path === '' ? '/' : path;
  };

  const basePath = getBasePath(currentPath);
  const cleanBasePath = basePath === '/' ? '' : basePath;

  // Generate robots directive
  const robotsDirective = noindex || nofollow
    ? `${noindex ? 'noindex' : 'index'}, ${nofollow ? 'nofollow' : 'follow'}`
    : 'index, follow';

  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="author" content={siteMetadata.author} />
        <meta name="keywords" content={pageKeywords} />
        <meta name="robots" content={robotsDirective} />

        {/* Google Search Console Verification */}
        <meta name="google-site-verification" content={siteMetadata.googleSiteVerification} />

        {/* Additional SEO Meta Tags */}
        <meta name="generator" content="Biodata360" />
        <meta name="application-name" content="Biodata360" />
        <meta name="msapplication-TileColor" content="#ffffff" />
        <meta name="msapplication-config" content="/browserconfig.xml" />

        {/* Canonical URL - Dynamic based on current path (No trailing slash) */}
        <link rel="canonical" href={`${siteMetadata.siteUrl}${cleanBasePath || ''}`} />

        {/* Hreflang Tags for SEO */}
        {SUPPORTED_LANGUAGES.map(lang => {
          const langPrefix = lang === DEFAULT_LANGUAGE ? '' : '/' + lang;
          return (
            <link
              key={lang}
              rel="alternate"
              hrefLang={lang}
              href={`${siteMetadata.siteUrl}${langPrefix}${cleanBasePath || ''}`}
            />
          );
        })}
        <link rel="alternate" hrefLang="x-default" href={`${siteMetadata.siteUrl}${cleanBasePath || ''}`} />

        {/* Open Graph Meta Tags - Dynamic content */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content={type} />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:image" content={pageImage} />
        <meta property="og:image:alt" content={pageTitle} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content={siteMetadata.title} />
        <meta property="og:locale" content="en_US" />
        <meta property="og:logo" content={siteMetadata.image} />

        {/* Twitter Card Meta Tags - Dynamic content */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content={siteMetadata.twitterUsername} />
        <meta name="twitter:creator" content={siteMetadata.twitterUsername} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={pageImage} />
        <meta name="twitter:image:alt" content={pageTitle} />

        {/* Additional SEO Meta Tags */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta http-equiv="Content-Type" content="text/html; charset=utf-8" />
        <meta name="theme-color" content="#ffffff" />
        <meta name="rating" content="general" />
        <meta name="revisit-after" content="7 days" />
        <meta name="language" content="English" />

        {/* Favicon and App Icons (using a single high-res icon) */}
        <link rel="icon" href={siteMetadata.favicon} type="image/png" />
        <link rel="shortcut icon" href={siteMetadata.favicon} type="image/png" />
        <link rel="apple-touch-icon" href={siteMetadata.favicon} />

        {/* Google Fonts */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap" rel="stylesheet" />
      </Helmet>

      {/* Structured Data */}
      <StructuredData schema={schema} />

      {/* Robots.txt and robots meta tag */}
      <Robot />
    </>
  );
};

export default SEOHead;
